import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faExpand, faCompress, faPenToSquare, faTrash } from '@fortawesome/free-solid-svg-icons';
import Sidebar from '../components/Sidebar';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import {
  getProductAvailabilityAndInUse,
  createNewProduct,
  createNewInventoryUnit,
  findStockItemRowIndex,
  stockDeployedToStatus,
  type ProductType,
  type InventoryUnitStatus,
  type InventoryUnitCondition,
} from '../data/inventory';
import { type OperationsInventoryRow } from '../data/timEInventoryList';
import { useInventory } from '../context/InventoryContext';
import './Page.css';
import './Inventory.css';
import BarcodeScanSection, { type BatchScanFeedback } from '../components/BarcodeScanSection';
import MonthlyInventoryReportModal from '../components/MonthlyInventoryReportModal';
import FfeInventoryReportModal from '../components/FfeInventoryReportModal';
import ProductUnitsReportModal from '../components/ProductUnitsReportModal';
import InventoryLogTab from '../components/InventoryLogTab';
import PurchaseOrderTab from '../components/inventory/PurchaseOrderTab';
import FileDropZone from '../components/FileDropZone';
import AttachmentVoidModal from '../components/AttachmentVoidModal';
import { AttachmentVoidPill, AttachmentVoidReason } from '../components/AttachmentVoidNote';
import { useAttachmentVoid } from '../hooks/useAttachmentVoid';
import { isVoided, patchAttachmentVoid, type AttachmentVoidFields } from '../api/attachmentVoid';
import { parseAssetTagBarcode, findUnitMatchingParsedBarcode, findUnitBySerialNumber } from '../utils/assetTagBarcode';
import { suggestProductsByTypeCode } from '../utils/productTypeMap';
import { SortableTh, StaticTh } from '../components/SortableTh';
import { useTableSortState } from '../hooks/useTableSort';
import { sortRows } from '../utils/tableSort';

/**
 * One captured barcode in a batch session, plus any extra fields the user
 * filled in via the per-item Edit form. `productId` is auto-resolved when the
 * scanned product-type code matches a known catalog product (see
 * productTypeMap); empty string when unresolved so the row can be flagged
 * for the user to assign a product before adding to inventory.
 */
const INVENTORY_TABS = [
  { id: 'inventory', label: 'Inventory' },
  { id: 'purchase-order', label: 'Purchase Order' },
  { id: 'log', label: 'Log' },
] as const;

type InventoryTabId = (typeof INVENTORY_TABS)[number]['id'];

type BatchScanItem = {
  id: string;
  rawText: string;
  countryOfOrigin: string;
  productType: string;
  serialNumber: string;
  productId: string;
  productName?: string;
  model?: string;
  manufacturer?: string;
  location?: string;
  business?: string;
  installationDate?: string;
  status?: InventoryUnitStatus;
  condition?: InventoryUnitCondition;
  trashBins?: string;
  linenBins?: string;
  rollingBases?: string;
  staticBases?: string;
};

function genLocalId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Middle segment of a scanned tag (USA-BIN-1234567890 → "BIN"); empty when the text isn't a tag. */
function assetTagCodeFromRawText(raw: string): string {
  return parseAssetTagBarcode(raw)?.productType.trim() ?? '';
}

/**
 * In-field: after merging batch defaults with per-row Edit values, every unit must have
 * non-empty business and location (same merge as save: row first, then batch).
 */
function batchInFieldPlacementComplete(items: BatchScanItem[], batchBusiness: string, batchLocation: string): boolean {
  if (items.length === 0) return false;
  const batchBus = batchBusiness.trim();
  const batchLoc = batchLocation.trim();
  return items.every((it) => {
    const loc = (it.location?.trim() || batchLoc) || '';
    const bus = (it.business?.trim() || batchBus) || '';
    return Boolean(bus) && Boolean(loc);
  });
}

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const MAX_FFE_FILE_SIZE = 2 * 1024 * 1024; // 2MB

function parseValue(s: string): number {
  const cleaned = String(s).replace(/[$,]/g, '').trim();
  return Number(cleaned) || 0;
}

function formatCurrency(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

const Inventory: React.FC = () => {
  const navigate = useNavigate();
  const { products, units, operations, timEParts, setProducts, setUnits, setOperations } = useInventory();
  /** Report generation flow: pick a report type, then (for inventory / FF&E) pick items. */
  const [reportFlow, setReportFlow] = useState<
    'closed' | 'choose' | 'inventory-select' | 'inventory' | 'ffe-select' | 'ffe' | 'tim-e' | 'bim-e'
  >('closed');
  /** Catalog product ids included in the inventory report (default: all active). */
  const [reportSelectedIds, setReportSelectedIds] = useState<Set<string>>(new Set());
  /** FF&E row indexes included in the FF&E report (default: every named item). */
  const [reportSelectedFfeIndexes, setReportSelectedFfeIndexes] = useState<Set<number>>(new Set());
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addModalChoice, setAddModalChoice] = useState<'new-product' | 'add-inventory' | null>(null);
  const [addInventoryEntryMode, setAddInventoryEntryMode] = useState<
    'choose' | 'scan-mode' | 'scan' | 'batch-scan' | 'batch-list' | 'form'
  >('choose');
  const [barcodeScanRemountKey, setBarcodeScanRemountKey] = useState(0);
  const [barcodeParseError, setBarcodeParseError] = useState<string | null>(null);
  /** Items captured during the current batch session. Cleared on modal close or after "Add to Inventory". */
  const [batchItems, setBatchItems] = useState<BatchScanItem[]>([]);
  /** When set, the form view is editing an existing batch item instead of creating a new unit. */
  const [editingBatchItemId, setEditingBatchItemId] = useState<string | null>(null);
  /** After batch scan: where units are being placed (drives default status and required fields). */
  const [batchPlacement, setBatchPlacement] = useState<'in-field' | 'warehouse'>('warehouse');
  const [batchBusiness, setBatchBusiness] = useState('');
  const [batchLocation, setBatchLocation] = useState('');
  /** Country of origin applied to the whole batch (no longer derived from the barcode). */
  const [batchCountryOfOrigin, setBatchCountryOfOrigin] = useState('');
  /** Catalog product applied to the whole batch (no longer derived from the barcode). */
  const [batchProductId, setBatchProductId] = useState('');
  /** 'batch' uses one product for every row; 'per-item' lets each scanned row carry its own. */
  const [batchProductMode, setBatchProductMode] = useState<'batch' | 'per-item'>('batch');
  /** Hides the tag-based recommendation until the next batch. */
  const [batchSuggestionDismissed, setBatchSuggestionDismissed] = useState(false);
  /** Product chosen in the recommendation banner; empty means "use the top match". */
  const [batchSuggestionPick, setBatchSuggestionPick] = useState('');
  /** Tag code that pre-filled the single-scan product field, so the form can flag it as a guess. */
  const [addInvProductSuggestedFrom, setAddInvProductSuggestedFrom] = useState('');
  /** Batch table + toolbar; used with Fullscreen API for reviewing many rows. */
  const batchListPanelFsRef = useRef<HTMLDivElement>(null);
  const [batchListPanelIsFullscreen, setBatchListPanelIsFullscreen] = useState(false);
  const [newProductName, setNewProductName] = useState('');
  const [newProductType, setNewProductType] = useState<ProductType>('Accessory');
  const [newProductSku, setNewProductSku] = useState('');
  const [newProductReplacementCost, setNewProductReplacementCost] = useState('');
  const [addInvProductId, setAddInvProductId] = useState('');
  const [addInvCountry, setAddInvCountry] = useState('');
  const [addInvProductType, setAddInvProductType] = useState('');
  const [addInvModel, setAddInvModel] = useState('');
  const [addInvSerial, setAddInvSerial] = useState('');
  const [addInvManufacturer, setAddInvManufacturer] = useState('');
  const [addInvLocation, setAddInvLocation] = useState('');
  const [addInvBusiness, setAddInvBusiness] = useState('');
  const [addInvInstallDate, setAddInvInstallDate] = useState('');
  const [addInvStatus, setAddInvStatus] = useState<InventoryUnitStatus | ''>('');
  const [addInvCondition, setAddInvCondition] = useState<InventoryUnitCondition | ''>('');
  const [addInvTrashBins, setAddInvTrashBins] = useState('');
  const [addInvLinenBins, setAddInvLinenBins] = useState('');
  const [addInvRollingBases, setAddInvRollingBases] = useState('');
  const [addInvStaticBases, setAddInvStaticBases] = useState('');
  const [addInvQuantity, setAddInvQuantity] = useState('1');
  /** Stock rows only: Yes = the items sit at a business, No = office/warehouse. */
  const [addInvStockDeployed, setAddInvStockDeployed] = useState(false);
  const [operationsEditIndex, setOperationsEditIndex] = useState<number | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
    products: false,
    ffeInventory: false,
  });
  const toggleSection = (key: string) =>
    setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: InventoryTabId = INVENTORY_TABS.some((t) => t.id === tabParam)
    ? (tabParam as InventoryTabId)
    : 'inventory';
  const selectTab = useCallback(
    (tab: InventoryTabId) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', tab);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );
  const [editProduct, setEditProduct] = useState('');
  const [editAvailable, setEditAvailable] = useState('');
  const [editReplacementCost, setEditReplacementCost] = useState('');
  const [editValue, setEditValue] = useState('');
  const [inventorySearchQuery, setInventorySearchQuery] = useState('');
  /** Shown when a scanned tag matches an existing inventory unit. */
  const [duplicateBarcodeInfo, setDuplicateBarcodeInfo] = useState<{
    productId: string;
    productName: string;
    serialNumber: string;
    countryOfOrigin: string;
    productType: string;
    unitId: string;
  } | null>(null);
  useEffect(() => {
    if (!addInvProductId) return;
    const sel = products.find((p) => p.id === addInvProductId);
    if (sel?.catalogStatus === 'Void') setAddInvProductId('');
  }, [products, addInvProductId]);

  type FfeDoc = AttachmentVoidFields & {
    id: string;
    fileName: string;
    type: 'receipt' | 'warranty';
    dataUrl: string;
  };
  /** FF&E documents for the currently open modal (item at operationsEditIndex). Loaded from API. */
  const [ffeDocumentsForEdit, setFfeDocumentsForEdit] = useState<FfeDoc[]>([]);

  useEffect(() => {
    const onFullscreenChange = () => {
      setBatchListPanelIsFullscreen(document.fullscreenElement === batchListPanelFsRef.current);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  useEffect(() => {
    if (!addModalOpen || addInventoryEntryMode !== 'batch-list') {
      const el = batchListPanelFsRef.current;
      if (el && document.fullscreenElement === el) {
        void document.exitFullscreen?.();
      }
    }
  }, [addModalOpen, addInventoryEntryMode]);

  useEffect(() => {
    if (operationsEditIndex === null) {
      setFfeDocumentsForEdit([]);
      return;
    }
    let cancelled = false;
    fetch(`${API_BASE}/api/ffe-documents?item_index=${operationsEditIndex}`)
      .then((res) => res.ok ? res.json() : Promise.reject(new Error('Failed to load documents')))
      .then((data: { documents?: FfeDoc[] }) => {
        if (!cancelled && Array.isArray(data?.documents)) setFfeDocumentsForEdit(data.documents);
      })
      .catch(() => {
        if (!cancelled) setFfeDocumentsForEdit([]);
      });
    return () => { cancelled = true; };
  }, [operationsEditIndex]);

  const ffeDocsSort = useTableSortState();
  // Voided receipts and warranties stay on the item, listed after the live ones.
  const sortedFfeDocumentsForEdit = useMemo(
    () =>
      sortRows(ffeDocumentsForEdit, ffeDocsSort.sortKey, ffeDocsSort.sortDir, (doc, key) => {
        switch (key) {
          case 'file':
            return doc.fileName;
          case 'type':
            return doc.type;
          default:
            return '';
        }
      }).sort((a, b) => Number(isVoided(a)) - Number(isVoided(b))),
    [ffeDocumentsForEdit, ffeDocsSort.sortKey, ffeDocsSort.sortDir]
  );

  const handleProductClick = (productId: string) => {
    navigate(`/inventory/product/${productId}`);
  };

  const handleBarcodeDecoded = useCallback(
    (text: string) => {
      const parsed = parseAssetTagBarcode(text);
      if (!parsed) {
        const raw = text.trim();
        const preview = raw.length > 120 ? `${raw.slice(0, 120)}…` : raw;
        setBarcodeParseError(
          `Could not parse this barcode. Expected format like USA-TIME-1234567890 (USA, product type, serial).${
            preview ? ` Received: ${preview}` : ''
          }`
        );
        setBarcodeScanRemountKey((k) => k + 1);
        return;
      }
      const existing = findUnitMatchingParsedBarcode(units, parsed);
      if (existing) {
        const product = products.find((p) => p.id === existing.productId);
        setDuplicateBarcodeInfo({
          productId: existing.productId,
          productName: product?.name ?? 'Unknown product',
          serialNumber: existing.serialNumber,
          countryOfOrigin: existing.countryOfOrigin,
          productType: existing.productType ?? '',
          unitId: existing.id,
        });
        setBarcodeParseError(null);
        setBarcodeScanRemountKey((k) => k + 1);
        return;
      }
      setBarcodeParseError(null);
      setAddInvCountry(parsed.countryOfOrigin);
      setAddInvProductType(parsed.productType);
      setAddInvSerial(parsed.serialNumber);
      /** Pre-select the product the tag code points at, or the closest active one by name. */
      const [suggested] = suggestProductsByTypeCode(parsed.productType, products);
      setAddInvProductId(suggested?.id ?? '');
      setAddInvProductSuggestedFrom(suggested ? parsed.productType : '');
      setEditingBatchItemId(null);
      setAddInventoryEntryMode('form');
    },
    [units, products]
  );

  /**
   * Batch-mode decode handler. Returns BatchScanFeedback so the scanner can
   * surface an inline banner without interrupting the operator. Same-text
   * repeats inside the session are flagged as 'duplicate'; existing inventory
   * units are flagged as 'existing' but still recorded so the operator can
   * review them in the Show Items table.
   */
  const handleBatchBarcodeDecoded = useCallback(
    (text: string): BatchScanFeedback => {
      const trimmed = text.trim();
      if (!trimmed) {
        return { kind: 'invalid', message: 'Empty scan — nothing to add' };
      }
      const dup = batchItems.find((it) => it.rawText === trimmed);
      if (dup) {
        return { kind: 'duplicate' };
      }
      /**
       * The barcode's parts are no longer treated as country / product type — the whole
       * scanned text is the serial. Country of origin and product come from the batch-level
       * fields and are applied at save time.
       */
      const newItem: BatchScanItem = {
        id: genLocalId(),
        rawText: trimmed,
        countryOfOrigin: '',
        productType: '',
        serialNumber: trimmed,
        productId: '',
      };
      setBatchItems((prev) => [...prev, newItem]);
      const existsInInventory = units.some(
        (u) => (u.serialNumber ?? '').trim().toLowerCase() === trimmed.toLowerCase()
      );
      if (existsInInventory) {
        return {
          kind: 'existing',
          message: 'Already in inventory — added to batch (review before saving)',
        };
      }
      return { kind: 'added' };
    },
    [batchItems, units]
  );

  const deleteBatchItem = useCallback((id: string) => {
    setBatchItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  /**
   * Tag codes present in the batch (USA-BIN-… → "BIN"), each with the products
   * they could refer to. Codes nothing matches are left out so the banner only
   * offers recommendations it can act on.
   */
  const batchSuggestionGroups = useMemo(() => {
    const byCode = new Map<string, { code: string; count: number }>();
    for (const it of batchItems) {
      const code = assetTagCodeFromRawText(it.rawText);
      if (!code) continue;
      const key = code.toLowerCase();
      const entry = byCode.get(key);
      if (entry) entry.count += 1;
      else byCode.set(key, { code, count: 1 });
    }
    return Array.from(byCode.values())
      .map(({ code, count }) => ({ code, count, matches: suggestProductsByTypeCode(code, products) }))
      .filter((group) => group.matches.length > 0)
      .sort((a, b) => b.count - a.count);
  }, [batchItems, products]);

  /** Best guess for a single row, used when filling the batch in per-item mode. */
  const suggestedProductForItem = useCallback(
    (item: BatchScanItem) => suggestProductsByTypeCode(assetTagCodeFromRawText(item.rawText), products)[0],
    [products]
  );

  const setBatchItemProduct = useCallback(
    (id: string, productId: string) => {
      const product = products.find((p) => p.id === productId);
      setBatchItems((prev) =>
        prev.map((it) => (it.id === id ? { ...it, productId, productName: product?.name } : it))
      );
    },
    [products]
  );

  /** Accept the recommendation for the whole batch. */
  const applySuggestedProductToBatch = useCallback((productId: string) => {
    setBatchProductId(productId);
    setBatchProductMode('batch');
    setBatchSuggestionDismissed(true);
  }, []);

  /** Decline one product for everything: each row keeps its own, pre-filled from its tag. */
  const applySuggestedProductsPerItem = useCallback(() => {
    setBatchItems((prev) =>
      prev.map((it) => {
        if (it.productId) return it;
        const best = suggestedProductForItem(it);
        return best ? { ...it, productId: best.id, productName: best.name } : it;
      })
    );
    setBatchProductMode('per-item');
    setBatchSuggestionDismissed(true);
  }, [suggestedProductForItem]);

  /** Product each row will be saved under: its own choice first, then the batch-level one. */
  const productIdForBatchItem = useCallback(
    (item: BatchScanItem) => item.productId || batchProductId,
    [batchProductId]
  );

  const resetBatchProductSuggestion = useCallback(() => {
    setBatchProductMode('batch');
    setBatchSuggestionDismissed(false);
    setBatchSuggestionPick('');
  }, []);

  /** Open the inventory form pre-populated from a batch item. On save, the item is updated (not a new unit). */
  const openBatchItemEdit = useCallback(
    (id: string) => {
      const item = batchItems.find((it) => it.id === id);
      if (!item) return;
      setEditingBatchItemId(item.id);
      /** Product & country are batch-level now; pre-fill them so per-row Edit stays consistent. */
      setAddInvProductId(item.productId || batchProductId);
      setAddInvCountry(item.countryOfOrigin || batchCountryOfOrigin);
      setAddInvProductType(item.productType);
      setAddInvSerial(item.serialNumber);
      setAddInvModel(item.model ?? '');
      setAddInvManufacturer(item.manufacturer ?? '');
      setAddInvLocation(item.location ?? '');
      setAddInvBusiness(item.business ?? '');
      setAddInvInstallDate(item.installationDate ?? '');
      setAddInvStatus(item.status ?? '');
      setAddInvCondition(item.condition ?? '');
      setAddInvTrashBins(item.trashBins ?? '');
      setAddInvLinenBins(item.linenBins ?? '');
      setAddInvRollingBases(item.rollingBases ?? '');
      setAddInvStaticBases(item.staticBases ?? '');
      setAddInvQuantity('1');
      setAddInventoryEntryMode('form');
    },
    [batchItems, batchProductId, batchCountryOfOrigin]
  );

  const openAddModal = useCallback(() => {
    setAddModalOpen(true);
    setAddModalChoice(null);
    setAddInventoryEntryMode('choose');
    setBarcodeParseError(null);
    setNewProductName('');
    setNewProductType('Accessory');
    setNewProductSku('');
    setNewProductReplacementCost('');
    setAddInvProductId('');
    setAddInvCountry('');
    setAddInvProductType('');
    setAddInvModel('');
    setAddInvSerial('');
    setAddInvManufacturer('');
    setAddInvLocation('');
    setAddInvBusiness('');
    setAddInvInstallDate('');
    setAddInvStatus('');
    setAddInvCondition('');
    setAddInvTrashBins('');
    setAddInvLinenBins('');
    setAddInvRollingBases('');
    setAddInvStaticBases('');
    setAddInvQuantity('1');
    setAddInvStockDeployed(false);
    setAddInvProductSuggestedFrom('');
    setDuplicateBarcodeInfo(null);
    setBatchItems([]);
    setEditingBatchItemId(null);
    setBatchPlacement('warehouse');
    setBatchBusiness('');
    setBatchLocation('');
    setBatchCountryOfOrigin('');
    setBatchProductId('');
    resetBatchProductSuggestion();
  }, [resetBatchProductSuggestion]);

  const closeAddModal = useCallback(() => {
    setAddModalOpen(false);
    setAddModalChoice(null);
    setAddInventoryEntryMode('choose');
    setBarcodeParseError(null);
    setDuplicateBarcodeInfo(null);
    setBatchItems([]);
    setEditingBatchItemId(null);
    setBatchPlacement('warehouse');
    setBatchBusiness('');
    setBatchLocation('');
    setBatchCountryOfOrigin('');
    setBatchProductId('');
    resetBatchProductSuggestion();
  }, [resetBatchProductSuggestion]);

  const toggleBatchTableFullscreen = useCallback(async () => {
    const el = batchListPanelFsRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement === el) {
        await document.exitFullscreen();
      } else {
        await el.requestFullscreen();
      }
    } catch {
      /* Unsupported or user denied */
    }
  }, []);

  /** Commit every batch item to inventory as one unit each. */
  const handleAddBatchToInventory = useCallback(() => {
    if (batchItems.length === 0) return;
    const missingProduct = batchItems.filter((it) => !productIdForBatchItem(it));
    if (missingProduct.length > 0) {
      alert(
        `${missingProduct.length} of ${batchItems.length} item(s) have no product. ` +
          'Pick a product for the whole batch, or set one on each row.'
      );
      return;
    }
    /** Block duplicates: any scanned serial already in inventory, or repeated within the batch. */
    const seenInBatch = new Set<string>();
    for (const it of batchItems) {
      const serial = (it.serialNumber ?? '').trim();
      const key = serial.toLowerCase();
      const existing = findUnitBySerialNumber(units, serial);
      if (existing) {
        const dupProduct = products.find((p) => p.id === existing.productId);
        alert(
          `Serial number "${serial}" is already in inventory` +
            `${dupProduct ? ` under ${dupProduct.name}` : ''}. ` +
            'Remove or edit that item before adding the batch.'
        );
        return;
      }
      if (key && seenInBatch.has(key)) {
        alert(`Serial number "${serial}" appears more than once in this batch. Remove the duplicate before saving.`);
        return;
      }
      if (key) seenInBatch.add(key);
    }
    const batchLoc = batchLocation.trim();
    const batchBus = batchBusiness.trim();
    if (batchPlacement === 'in-field' && !batchInFieldPlacementComplete(batchItems, batchBusiness, batchLocation)) {
      alert(
        'In-field: each unit needs a business and a location after merging the fields above with Edit. ' +
          'For example, set both here for the whole batch, or only on each row, or mix (e.g. one business above and different locations per row).'
      );
      return;
    }
    const bulkStatus: InventoryUnitStatus = batchPlacement === 'in-field' ? 'Deployed' : 'In Storage';
    const batchCountry = batchCountryOfOrigin.trim();
    const newUnits = batchItems.map((it) => {
      const loc = (it.location?.trim() || batchLoc) || undefined;
      const bus =
        batchPlacement === 'in-field'
          ? (it.business?.trim() || batchBus) || undefined
          : it.business?.trim() || undefined;
      /**
       * Country of origin comes from the batch-level field; the product is the row's own when
       * it has one (per-item mode or a row Edit), otherwise the batch-level pick. The serial is
       * the raw scanned barcode saved as-is — no product type, and no country/type prefix.
       */
      const unit = createNewInventoryUnit(productIdForBatchItem(it), batchCountry, (it.model ?? '').trim(), '', {
        manufacturer: it.manufacturer?.trim() || undefined,
        location: loc,
        business: bus,
        installationDate: it.installationDate || undefined,
        status: bulkStatus,
        condition: it.condition,
        trashBins: it.trashBins != null && it.trashBins !== '' ? parseInt(it.trashBins, 10) : undefined,
        linenBins: it.linenBins != null && it.linenBins !== '' ? parseInt(it.linenBins, 10) : undefined,
        rollingBases:
          it.rollingBases != null && it.rollingBases !== '' ? parseInt(it.rollingBases, 10) : undefined,
        staticBases:
          it.staticBases != null && it.staticBases !== '' ? parseInt(it.staticBases, 10) : undefined,
      });
      unit.serialNumber = (it.serialNumber ?? '').trim();
      return unit;
    });
    setUnits([...units, ...newUnits]);
    closeAddModal();
  }, [batchItems, batchPlacement, batchBusiness, batchLocation, batchCountryOfOrigin, productIdForBatchItem, products, units, setUnits, closeAddModal]);

  const batchListSaveReady = useMemo(
    () =>
      batchItems.length > 0 &&
      batchItems.every((it) => productIdForBatchItem(it)) &&
      (batchPlacement !== 'in-field' || batchInFieldPlacementComplete(batchItems, batchBusiness, batchLocation)),
    [batchItems, productIdForBatchItem, batchPlacement, batchBusiness, batchLocation]
  );

  /** Reset the form fields used by both single-scan and batch-edit. */
  const resetInventoryForm = useCallback(() => {
    setAddInvProductId('');
    setAddInvCountry('');
    setAddInvProductType('');
    setAddInvModel('');
    setAddInvSerial('');
    setAddInvManufacturer('');
    setAddInvLocation('');
    setAddInvBusiness('');
    setAddInvInstallDate('');
    setAddInvStatus('');
    setAddInvCondition('');
    setAddInvTrashBins('');
    setAddInvLinenBins('');
    setAddInvRollingBases('');
    setAddInvStaticBases('');
    setAddInvQuantity('1');
    setAddInvStockDeployed(false);
    setAddInvProductSuggestedFrom('');
  }, []);

  const handleAddNewProduct = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProductName.trim()) return;
    setProducts([
      ...products,
      createNewProduct(newProductName.trim(), newProductType, newProductSku, newProductReplacementCost),
    ]);
    closeAddModal();
  };

  const handleAddInventory = (e: React.FormEvent) => {
    e.preventDefault();
    if (!addInvProductId) return;

    const selectedProductEarly = products.find((p) => p.id === addInvProductId);
    if (selectedProductEarly?.type === 'Stock Item' && !editingBatchItemId) {
      const qty = Math.max(1, parseInt(addInvQuantity, 10) || 1);
      const mfr = addInvManufacturer.trim();
      const loc = addInvLocation.trim();
      const idx = findStockItemRowIndex(units, addInvProductId, mfr, loc, addInvStockDeployed);
      if (idx >= 0) {
        setUnits(
          units.map((x, i) =>
            i === idx ? { ...x, stockQuantity: (x.stockQuantity ?? 1) + qty } : x
          )
        );
      } else {
        const newUnit = createNewInventoryUnit(addInvProductId, '', '', '', {
          manufacturer: mfr || undefined,
          location: loc || undefined,
          stockQuantity: qty,
          status: stockDeployedToStatus(addInvStockDeployed),
        });
        setUnits([...units, newUnit]);
      }
      closeAddModal();
      return;
    }

    /** Editing a batch item: update the row in place and return to the batch list (no new unit created). */
    if (editingBatchItemId) {
      const selectedProduct = products.find((p) => p.id === addInvProductId);
      setBatchItems((prev) =>
        prev.map((it) =>
          it.id === editingBatchItemId
            ? {
                ...it,
                productId: addInvProductId,
                productName: selectedProduct?.name,
                countryOfOrigin: addInvCountry.trim(),
                productType: addInvProductType.trim(),
                serialNumber: addInvSerial.trim(),
                model: addInvModel.trim(),
                manufacturer: addInvManufacturer.trim(),
                location: addInvLocation.trim(),
                business: addInvBusiness.trim(),
                installationDate: addInvInstallDate,
                status: (addInvStatus as InventoryUnitStatus) || undefined,
                condition: (addInvCondition as InventoryUnitCondition) || undefined,
                trashBins: addInvTrashBins,
                linenBins: addInvLinenBins,
                rollingBases: addInvRollingBases,
                staticBases: addInvStaticBases,
              }
            : it
        )
      );
      setEditingBatchItemId(null);
      resetInventoryForm();
      setAddInventoryEntryMode('batch-list');
      return;
    }

    const selectedProduct = products.find((p) => p.id === addInvProductId);
    const isFullProduct = !!selectedProduct && (selectedProduct.name === 'TIM-E Bot' || selectedProduct.name === 'BIM-E bot');
    if (isFullProduct) {
      const newUnit = createNewInventoryUnit(
        addInvProductId,
        addInvCountry.trim(),
        addInvModel.trim(),
        addInvSerial.trim(),
        {
          productType: addInvProductType.trim() || undefined,
          manufacturer: addInvManufacturer.trim() || undefined,
          location: addInvLocation.trim() || undefined,
          business: addInvBusiness.trim() || undefined,
          installationDate: addInvInstallDate || undefined,
          status: (addInvStatus as InventoryUnitStatus) || undefined,
          condition: (addInvCondition as InventoryUnitCondition) || undefined,
          trashBins: addInvTrashBins !== '' ? parseInt(addInvTrashBins, 10) : undefined,
          linenBins: addInvLinenBins !== '' ? parseInt(addInvLinenBins, 10) : undefined,
          rollingBases: addInvRollingBases !== '' ? parseInt(addInvRollingBases, 10) : undefined,
          staticBases: addInvStaticBases !== '' ? parseInt(addInvStaticBases, 10) : undefined,
        }
      );
      const dup = findUnitBySerialNumber(units, newUnit.serialNumber);
      if (dup) {
        const dupProduct = products.find((p) => p.id === dup.productId);
        alert(
          `Serial number "${newUnit.serialNumber}" is already in inventory` +
            `${dupProduct ? ` under ${dupProduct.name}` : ''}. ` +
            'Use a different serial number to avoid a duplicate.'
        );
        return;
      }
      setUnits([...units, newUnit]);
    } else {
      const qty = Math.max(1, parseInt(addInvQuantity, 10) || 1);
      const baseSerial = addInvSerial.trim() || selectedProduct?.name || 'Item';
      const newUnits: ReturnType<typeof createNewInventoryUnit>[] = [];
      const statusVal = (addInvStatus as InventoryUnitStatus) || undefined;
      const conditionVal = (addInvCondition as InventoryUnitCondition) || undefined;
      for (let i = 0; i < qty; i++) {
        const serial = qty > 1 ? `${baseSerial}-${i + 1}` : baseSerial;
        newUnits.push(
          createNewInventoryUnit(
            addInvProductId,
            addInvCountry.trim(),
            addInvModel.trim(),
            serial,
            {
              productType: addInvProductType.trim() || undefined,
              manufacturer: addInvManufacturer.trim() || undefined,
              location: addInvLocation.trim() || undefined,
              business: addInvBusiness.trim() || undefined,
              installationDate: addInvInstallDate || undefined,
              status: statusVal,
              condition: conditionVal,
            }
          )
        );
      }
      const dupUnit = newUnits.find((u) => findUnitBySerialNumber(units, u.serialNumber));
      if (dupUnit) {
        const existing = findUnitBySerialNumber(units, dupUnit.serialNumber);
        const dupProduct = existing && products.find((p) => p.id === existing.productId);
        alert(
          `Serial number "${dupUnit.serialNumber}" is already in inventory` +
            `${dupProduct ? ` under ${dupProduct.name}` : ''}. ` +
            'Use a different serial number to avoid a duplicate.'
        );
        return;
      }
      setUnits([...units, ...newUnits]);
    }
    closeAddModal();
  };

  const openOperationsEdit = (index: number) => {
    const row = operations[index];
    if (!row) return;
    setOperationsEditIndex(index);
    setEditProduct(row.product);
    setEditAvailable(row.available);
    setEditReplacementCost(row.replacementCostPerItem);
    setEditValue(row.value);
  };

  const closeOperationsEdit = () => {
    setOperationsEditIndex(null);
  };

  const handleSaveOperationsEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (operationsEditIndex === null) return;
    const next = [...operations];
    next[operationsEditIndex] = {
      product: editProduct.trim(),
      available: editAvailable.trim(),
      replacementCostPerItem: editReplacementCost.trim(),
      value: editValue.trim(),
    };
    setOperations(next);
    closeOperationsEdit();
  };

  const handleAddFfeItem = () => {
    const newRow: OperationsInventoryRow = { product: '', available: '', replacementCostPerItem: '', value: '' };
    const next = [...operations, newRow];
    setOperations(next);
    setOperationsEditIndex(next.length - 1);
    setEditProduct('');
    setEditAvailable('');
    setEditReplacementCost('');
    setEditValue('');
  };

  const uploadFfeDoc = useCallback(
    (file: File, type: 'receipt' | 'warranty') => {
      if (operationsEditIndex === null) return;
      if (file.size > MAX_FFE_FILE_SIZE) {
        alert('File is too large. Maximum size is 2 MB.');
        return;
      }
      const reader = new FileReader();
      const itemIndex = operationsEditIndex;
      reader.onload = async () => {
        const dataUrl = reader.result as string;
        try {
          const res = await fetch(`${API_BASE}/api/ffe-documents`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              item_index: itemIndex,
              file_name: file.name,
              type,
              file_data: dataUrl,
            }),
          });
          if (!res.ok) throw new Error(await res.json().then((d: { error?: string }) => d.error).catch(() => 'Upload failed'));
          const doc: FfeDoc = await res.json();
          setFfeDocumentsForEdit((prev) => [...prev, doc]);
        } catch (err) {
          alert(err instanceof Error ? err.message : 'Failed to upload document.');
        }
      };
      reader.readAsDataURL(file);
    },
    [operationsEditIndex]
  );

  const ffeDocVoid = useAttachmentVoid<FfeDoc>({
    noun: 'document',
    label: (doc) => doc.fileName,
    onVoid: async (doc, reason) => {
      const saved = await patchAttachmentVoid<Partial<FfeDoc>>(`${API_BASE}/api/ffe-documents/${doc.id}`, reason);
      setFfeDocumentsForEdit((prev) => prev.map((d) => (d.id === doc.id ? { ...d, ...saved } : d)));
    },
    onUnvoid: async (doc) => {
      const saved = await patchAttachmentVoid<Partial<FfeDoc>>(`${API_BASE}/api/ffe-documents/${doc.id}`, null);
      setFfeDocumentsForEdit((prev) => prev.map((d) => (d.id === doc.id ? { ...d, ...saved } : d)));
    },
    onError: (message) => alert(message),
  });

  const operationsTotalValue = operations.reduce((sum, r) => sum + parseValue(r.value), 0);

  /** Active products available to pick for the inventory report, sorted A–Z. */
  const reportSelectableProducts = useMemo(
    () =>
      products
        .filter((p) => p.catalogStatus !== 'Void')
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
    [products]
  );

  /** Catalog products passed to the monthly report: only the ones still checked. */
  const reportFilteredProducts = useMemo(
    () => products.filter((p) => reportSelectedIds.has(p.id)),
    [products, reportSelectedIds]
  );

  /** Voided parts are retired stock, so they stay off the monthly report. */
  const reportTimEParts = useMemo(() => timEParts.filter((r) => r.status !== 'Void'), [timEParts]);

  const timEProduct = useMemo(() => products.find((p) => p.name === 'TIM-E Bot'), [products]);
  const bimEProduct = useMemo(() => products.find((p) => p.name === 'BIM-E bot'), [products]);
  const timEReportUnits = useMemo(
    () => (timEProduct ? units.filter((u) => u.productId === timEProduct.id) : []),
    [timEProduct, units]
  );
  const bimEReportUnits = useMemo(
    () => (bimEProduct ? units.filter((u) => u.productId === bimEProduct.id) : []),
    [bimEProduct, units]
  );

  const closeReportFlow = useCallback(() => setReportFlow('closed'), []);

  const startInventoryReport = useCallback(() => {
    setReportSelectedIds(new Set(reportSelectableProducts.map((p) => p.id)));
    setReportFlow('inventory-select');
  }, [reportSelectableProducts]);

  /** Named FF&E rows, in table order — blank "new item" rows stay off the report picker. */
  const reportSelectableFfe = useMemo(
    () =>
      operations
        .map((row, index) => ({ row, index }))
        .filter(({ row }) => row.product.trim() !== ''),
    [operations]
  );

  const reportFilteredFfe = useMemo(
    () => operations.filter((_, index) => reportSelectedFfeIndexes.has(index)),
    [operations, reportSelectedFfeIndexes]
  );

  const startFfeReport = useCallback(() => {
    setReportSelectedFfeIndexes(new Set(reportSelectableFfe.map(({ index }) => index)));
    setReportFlow('ffe-select');
  }, [reportSelectableFfe]);

  const toggleReportProduct = useCallback((id: string) => {
    setReportSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const setAllReportProducts = useCallback(
    (checked: boolean) => {
      setReportSelectedIds(checked ? new Set(reportSelectableProducts.map((p) => p.id)) : new Set());
    },
    [reportSelectableProducts]
  );

  const toggleReportFfeItem = useCallback((index: number) => {
    setReportSelectedFfeIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }, []);

  const setAllReportFfeItems = useCallback(
    (checked: boolean) => {
      setReportSelectedFfeIndexes(checked ? new Set(reportSelectableFfe.map(({ index }) => index)) : new Set());
    },
    [reportSelectableFfe]
  );

  const inventorySearchLower = inventorySearchQuery.trim().toLowerCase();
  /** Rank for the featured (starred) robots so they sit at the top in a fixed order. */
  const starRank = (name: string) => {
    if (name === 'TIM-E Bot') return 0;
    if (name === 'BIM-E bot') return 1;
    if (name === 'Kebb-i') return 2;
    return Number.MAX_SAFE_INTEGER;
  };

  /** Product ids that have at least one unit whose serial number (current or temporary) matches the query. */
  const productIdsMatchingSerial = useMemo(() => {
    if (!inventorySearchLower) return new Set<string>();
    const ids = new Set<string>();
    for (const u of units) {
      if (
        (u.serialNumber ?? '').toLowerCase().includes(inventorySearchLower) ||
        (u.tempSerialNumber ?? '').toLowerCase().includes(inventorySearchLower)
      ) {
        ids.add(u.productId);
      }
    }
    return ids;
  }, [units, inventorySearchLower]);

  const filteredProducts = useMemo(() => {
    const list = inventorySearchLower
      ? products.filter(
          (p) =>
            [p.name, p.type, p.sku ?? '', p.catalogStatus === 'Void' ? 'void' : 'active'].some((v) =>
              v.toLowerCase().includes(inventorySearchLower)
            ) || productIdsMatchingSerial.has(p.id)
        )
      : products;
    return [...list].sort((a, b) => starRank(a.name) - starRank(b.name));
  }, [products, inventorySearchLower, productIdsMatchingSerial]);

  const inventoryProductsSort = useTableSortState();
  const sortedFilteredProducts = useMemo(
    () =>
      sortRows(filteredProducts, inventoryProductsSort.sortKey, inventoryProductsSort.sortDir, (item, key) => {
        const { availability, inUse, unavailable } = getProductAvailabilityAndInUse(item.id, units, item.type);
        const avNum = typeof availability === 'number' ? availability : parseValue(String(availability));
        const useNum = typeof inUse === 'number' ? inUse : parseValue(String(inUse));
        const unNum = typeof unavailable === 'number' ? unavailable : parseValue(String(unavailable));
        switch (key) {
          case 'name':
            return item.name ?? '';
          case 'type':
            return item.type ?? '';
          case 'sku':
            return item.sku ?? '';
          case 'availability':
            return Number.isFinite(avNum) ? avNum : String(availability);
          case 'inUse':
            return Number.isFinite(useNum) ? useNum : String(inUse);
          case 'unavailable':
            return Number.isFinite(unNum) ? unNum : String(unavailable);
          case 'replacementCost': {
            const costNum = parseValue(String(item.replacementCostPerItem ?? ''));
            return Number.isFinite(costNum) && item.replacementCostPerItem ? costNum : -Infinity;
          }
          case 'catalogStatus':
            return item.catalogStatus === 'Void' ? 'Void' : 'Active';
          default:
            return '';
        }
      }),
    [filteredProducts, inventoryProductsSort.sortKey, inventoryProductsSort.sortDir, units]
  );

  const addModalTitle = (() => {
    if (addModalChoice === 'new-product') return 'Add Product';
    if (addModalChoice !== 'add-inventory') return 'Add Product';
    switch (addInventoryEntryMode) {
      case 'scan-mode':
        return 'Choose scan mode';
      case 'scan':
        return 'Scan barcode';
      case 'batch-scan':
        return `Batch scan${batchItems.length > 0 ? ` (${batchItems.length})` : ''}`;
      case 'batch-list':
        return `Scanned items (${batchItems.length})`;
      case 'form':
        return editingBatchItemId ? 'Edit batch item' : 'Add inventory';
      default:
        return 'Add inventory';
    }
  })();

  return (
    <div className="page-container">
      <div className="page-layout">
        <Sidebar />
        <main className="page-main">

          <div>
            <PageHeader title="Inventory" subtitle="Track and manage inventory items" />
          </div>

          <div className="page-content">

            <div className="inventory-subtabs" role="tablist" aria-label="Inventory sections">
              {INVENTORY_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  className={`inventory-subtab${activeTab === tab.id ? ' inventory-subtab--active' : ''}`}
                  onClick={() => selectTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === 'log' ? (
              <InventoryLogTab />
            ) : activeTab === 'purchase-order' ? (
              <PurchaseOrderTab />
            ) : (
              <>
            <div className="page-toolbar">
              <input
                type="text"
                className="page-toolbar-search"
                placeholder="Search inventory or serial number..."
                value={inventorySearchQuery}
                onChange={(e) => setInventorySearchQuery(e.target.value)}
                aria-label="Search inventory or serial number"
              />
              <button type="button" className="page-toolbar-btn" onClick={() => setReportFlow('choose')}>
                Generate report
              </button>
              <button type="button" className="page-toolbar-btn" onClick={openAddModal}>
                +&nbsp; Add Product
              </button>
            </div>

            <div className="inventory-table-section">
              <div className="collapsible-header" onClick={() => toggleSection('products')}>
                <h3 className="inventory-section-title">Products</h3>
                <span className={`collapse-arrow${!collapsedSections.products ? ' collapse-arrow--open' : ''}`}>▶</span>
              </div>
              {!collapsedSections.products && (
                <>
                  <div className="inventory-products-table-wrapper">
                    <table className="inventory-table">
                      <thead>
                        <tr>
                          <SortableTh label="Product" columnKey="name" sortKey={inventoryProductsSort.sortKey} sortDir={inventoryProductsSort.sortDir} onSort={inventoryProductsSort.toggleSort} />
                          <SortableTh label="Type" columnKey="type" sortKey={inventoryProductsSort.sortKey} sortDir={inventoryProductsSort.sortDir} onSort={inventoryProductsSort.toggleSort} />
                          <SortableTh label="Status" columnKey="catalogStatus" sortKey={inventoryProductsSort.sortKey} sortDir={inventoryProductsSort.sortDir} onSort={inventoryProductsSort.toggleSort} />
                          <SortableTh label="Availability" columnKey="availability" sortKey={inventoryProductsSort.sortKey} sortDir={inventoryProductsSort.sortDir} onSort={inventoryProductsSort.toggleSort} />
                          <SortableTh label="In-Use" columnKey="inUse" sortKey={inventoryProductsSort.sortKey} sortDir={inventoryProductsSort.sortDir} onSort={inventoryProductsSort.toggleSort} />
                          <SortableTh label="Unavailable" columnKey="unavailable" sortKey={inventoryProductsSort.sortKey} sortDir={inventoryProductsSort.sortDir} onSort={inventoryProductsSort.toggleSort} />
                          <SortableTh label="Replacement Costs" columnKey="replacementCost" sortKey={inventoryProductsSort.sortKey} sortDir={inventoryProductsSort.sortDir} onSort={inventoryProductsSort.toggleSort} />
                        </tr>
                      </thead>
                      <tbody>
                        {sortedFilteredProducts.map((item) => {
                          const { availability, inUse, unavailable } = getProductAvailabilityAndInUse(
                            item.id,
                            units,
                            item.type
                          );
                          return (
                            <tr
                              key={item.id}
                              className={item.catalogStatus === 'Void' ? 'inventory-product-row--void' : undefined}
                              onClick={() => handleProductClick(item.id)}
                              style={{ cursor: 'pointer' }}
                            >
                              <td data-label="Product">
                                {(item.name === 'TIM-E Bot' || item.name === 'BIM-E bot' || item.name === 'Kebb-i') && (
                                  <span className="inventory-product-star" aria-label="Featured">★ </span>
                                )}
                                {item.name}
                              </td>
                              <td data-label="Type">{item.type}</td>
                              <td data-label="Status">{item.catalogStatus === 'Void' ? 'Void' : 'Active'}</td>
                              <td data-label="Availability">{availability}</td>
                              <td data-label="In-Use">{inUse}</td>
                              <td data-label="Unavailable">{unavailable}</td>
                              <td data-label="Replacement Costs">{item.replacementCostPerItem || '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {filteredProducts.length === 0 && products.length > 0 && (
                    <p className="page-subtitle">No inventory items match your search.</p>
                  )}
                </>
              )}
            </div>

            <div className="inventory-table-section">
              <div className="inventory-ffe-header">
                <div className="collapsible-header" onClick={() => toggleSection('ffeInventory')}>
                  <h3 className="inventory-section-title">Furniture, Fixtures, and Equipment Inventory</h3>
                  <span className={`collapse-arrow${!collapsedSections.ffeInventory ? ' collapse-arrow--open' : ''}`}>▶</span>
                </div>
                <button type="button" className="inventory-add-product-button inventory-add-ffe-button" onClick={handleAddFfeItem}>
                  + Add FF&E Item
                </button>
              </div>
              {!collapsedSections.ffeInventory && <div className="inventory-table-wrapper inventory-list-table-wrapper">
                <table className="inventory-table inventory-list-table inventory-table-fit">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Available</th>
                      <th>Replacement Cost per item</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {operations.map((row, index) => (
                      <tr
                        key={index}
                        className="inventory-list-data-row"
                        onClick={() => openOperationsEdit(index)}
                      >
                        <td data-label="Product">{row.product}</td>
                        <td data-label="Available">{row.available || '—'}</td>
                        <td data-label="Replacement Cost">{row.replacementCostPerItem || '—'}</td>
                        <td data-label="Value">{row.value || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="inventory-list-total-row">
                      <td colSpan={3}>Total</td>
                      <td>{formatCurrency(operationsTotalValue)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>}
            </div>

              </>
            )}
          </div>
        </main>
      </div>

      {/* Add Product / Add Inventory modal */}
      <Modal isOpen={addModalOpen} onClose={closeAddModal} title={addModalTitle} wide={addModalChoice === 'add-inventory'}>
              {addModalChoice === null && (
                <div className="modal-body">
                <div className="inventory-add-choices">
                  <p className="inventory-add-prompt">What would you like to do?</p>
                  <button
                    type="button"
                    className="inventory-add-choice-button"
                    onClick={() => setAddModalChoice('new-product')}
                  >
                    Add a new product
                  </button>
                  <button
                    type="button"
                    className="inventory-add-choice-button"
                    onClick={() => {
                      setAddModalChoice('add-inventory');
                      setAddInventoryEntryMode('choose');
                      setBarcodeParseError(null);
                    }}
                  >
                    Add inventory
                  </button>
                </div>
                </div>
              )}

              {addModalChoice === 'new-product' && (
                <form onSubmit={handleAddNewProduct} className="inventory-add-form">
                  <div className="modal-body">
                  <div className="form-group">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={newProductName}
                      onChange={(e) => setNewProductName(e.target.value)}
                      placeholder="Product name"
                      required
                      autoFocus
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Type</label>
                    <select
                      className="form-input"
                      value={newProductType}
                      onChange={(e) => setNewProductType(e.target.value as ProductType)}
                    >
                      <option value="Robot">Robot</option>
                      <option value="Accessory">Accessories</option>
                      <option value="Stock Item">Stock Item</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">SKU <span style={{ fontWeight: 400, color: '#8A8F93' }}>(optional)</span></label>
                    <input
                      type="text"
                      className="form-input"
                      value={newProductSku}
                      onChange={(e) => setNewProductSku(e.target.value)}
                      placeholder="e.g. TIM-E-001"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Replacement cost <span style={{ fontWeight: 400, color: '#8A8F93' }}>(optional)</span></label>
                    <input
                      type="text"
                      className="form-input"
                      value={newProductReplacementCost}
                      onChange={(e) => setNewProductReplacementCost(e.target.value)}
                      placeholder="e.g. $1,200"
                    />
                  </div>
                  </div>
                  <div className="modal-actions">
                    <button type="button" className="cancel-button" onClick={closeAddModal}>Cancel</button>
                    <button type="submit" className="save-button">Add Product</button>
                  </div>
                </form>
              )}

              {addModalChoice === 'add-inventory' && addInventoryEntryMode === 'choose' && (
                <>
                  <div className="modal-body">
                    <div className="inventory-add-choices">
                      <p className="inventory-add-prompt">How would you like to add this item?</p>
                      <button
                        type="button"
                        className="inventory-add-choice-button"
                        onClick={() => {
                          setBarcodeParseError(null);
                          setAddInventoryEntryMode('scan-mode');
                        }}
                      >
                        Scan barcode
                      </button>
                      <button
                        type="button"
                        className="inventory-add-choice-button"
                        onClick={() => {
                          resetInventoryForm();
                          setEditingBatchItemId(null);
                          setAddInventoryEntryMode('form');
                        }}
                      >
                        Add manually
                      </button>
                    </div>
                  </div>
                  <div className="modal-actions">
                    <button type="button" className="cancel-button" onClick={closeAddModal}>
                      Cancel
                    </button>
                  </div>
                </>
              )}

              {addModalChoice === 'add-inventory' && addInventoryEntryMode === 'scan-mode' && (
                <>
                  <div className="modal-body">
                    <div className="inventory-add-choices">
                      <p className="inventory-add-prompt">Single scan or batch?</p>
                      <button
                        type="button"
                        className="inventory-add-choice-button"
                        onClick={() => {
                          setBarcodeParseError(null);
                          setAddInventoryEntryMode('scan');
                        }}
                      >
                        Single scan
                        <span className="inventory-add-choice-sub">Scan one barcode and fill in details.</span>
                      </button>
                      <button
                        type="button"
                        className="inventory-add-choice-button"
                        onClick={() => {
                          setBarcodeParseError(null);
                          setBatchItems([]);
                          setBatchPlacement('warehouse');
                          setBatchBusiness('');
                          setBatchLocation('');
                          setBatchCountryOfOrigin('');
                          setBatchProductId('');
                          resetBatchProductSuggestion();
                          setAddInventoryEntryMode('batch-scan');
                        }}
                      >
                        Batch
                        <span className="inventory-add-choice-sub">
                          Scan many barcodes back-to-back, then review and add them all at once.
                        </span>
                      </button>
                    </div>
                  </div>
                  <div className="modal-actions">
                    <button
                      type="button"
                      className="cancel-button"
                      onClick={() => setAddInventoryEntryMode('choose')}
                    >
                      Back
                    </button>
                    <button type="button" className="cancel-button" onClick={closeAddModal}>
                      Cancel
                    </button>
                  </div>
                </>
              )}

              {addModalChoice === 'add-inventory' && addInventoryEntryMode === 'scan' && (
                <div className="modal-body">
                  {barcodeParseError && (
                    <p className="inventory-barcode-parse-error" role="alert">
                      {barcodeParseError}
                    </p>
                  )}
                  <BarcodeScanSection
                    remountKey={barcodeScanRemountKey}
                    onDecoded={handleBarcodeDecoded}
                    onBack={() => {
                      setBarcodeParseError(null);
                      setAddInventoryEntryMode('scan-mode');
                    }}
                  />
                </div>
              )}

              {addModalChoice === 'add-inventory' && addInventoryEntryMode === 'batch-scan' && (
                <div className="modal-body">
                  <BarcodeScanSection
                    remountKey={barcodeScanRemountKey}
                    batchMode
                    onDecoded={handleBatchBarcodeDecoded}
                    onBack={() => setAddInventoryEntryMode('scan-mode')}
                    toolbarBeforeLog={
                      <div className="inventory-batch-toolbar">
                        <button
                          type="button"
                          className="save-button inventory-batch-show-btn"
                          onClick={() => setAddInventoryEntryMode('batch-list')}
                          disabled={batchItems.length === 0}
                        >
                          Show items ({batchItems.length})
                        </button>
                      </div>
                    }
                  />
                </div>
              )}

              {addModalChoice === 'add-inventory' && addInventoryEntryMode === 'batch-list' && (
                <>
                  <div className="modal-body">
                    {batchItems.length === 0 ? (
                      <p className="page-subtitle" style={{ margin: 0 }}>
                        No items in this batch yet. Go back to the scanner to add some.
                      </p>
                    ) : (
                      <>
                        {batchSuggestionGroups.length > 0 && !batchSuggestionDismissed && !batchProductId && (() => {
                          const single = batchSuggestionGroups.length === 1 ? batchSuggestionGroups[0] : null;
                          const pick = single
                            ? single.matches.find((p) => p.id === batchSuggestionPick)?.id ?? single.matches[0].id
                            : '';
                          return (
                            <div className="inventory-batch-suggestion" role="region" aria-label="Product recommendation">
                              <span className="inventory-batch-placement-heading">Suggested product</span>
                              <p className="inventory-batch-placement-hint">
                                {single
                                  ? `All ${single.count} scanned tag${single.count === 1 ? '' : 's'} use the ${single.code} code, which points at these products.`
                                  : `These tags cover ${batchSuggestionGroups.length} codes (${batchSuggestionGroups
                                      .map((g) => `${g.code} ×${g.count}`)
                                      .join(', ')}), so one product will not fit the whole batch.`}
                              </p>
                              {single ? (
                                <div className="inventory-batch-assign-row">
                                  <select
                                    className="form-input inventory-batch-assign-select"
                                    aria-label={`Product suggested for ${single.code} tags`}
                                    value={pick}
                                    onChange={(e) => setBatchSuggestionPick(e.target.value)}
                                  >
                                    {single.matches.map((p) => (
                                      <option key={p.id} value={p.id}>
                                        {p.name} ({p.type})
                                      </option>
                                    ))}
                                  </select>
                                  <button
                                    type="button"
                                    className="save-button inventory-batch-assign-btn"
                                    onClick={() => applySuggestedProductToBatch(pick)}
                                  >
                                    Apply to all {batchItems.length}
                                  </button>
                                  <button
                                    type="button"
                                    className="cancel-button inventory-batch-assign-btn"
                                    onClick={applySuggestedProductsPerItem}
                                  >
                                    Set per item
                                  </button>
                                  <button
                                    type="button"
                                    className="cancel-button inventory-batch-assign-btn"
                                    onClick={() => setBatchSuggestionDismissed(true)}
                                  >
                                    Dismiss
                                  </button>
                                </div>
                              ) : (
                                <div className="inventory-batch-assign-row">
                                  <button
                                    type="button"
                                    className="save-button inventory-batch-assign-btn"
                                    onClick={applySuggestedProductsPerItem}
                                  >
                                    Set suggestions per item
                                  </button>
                                  <button
                                    type="button"
                                    className="cancel-button inventory-batch-assign-btn"
                                    onClick={() => setBatchSuggestionDismissed(true)}
                                  >
                                    Dismiss
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                        <div className="inventory-batch-placement" role="region" aria-label="Country of origin and product for this batch">
                          <span className="inventory-batch-placement-heading">Country of origin &amp; product</span>
                          <p className="inventory-batch-placement-hint">
                            {batchProductMode === 'per-item'
                              ? 'Country of origin applies to every item. Each row carries its own product; the product below covers any row you leave unset.'
                              : 'Country of origin applies to every item, and so does the product except on rows that carry their own. The scanned barcode is saved as the serial number as-is.'}
                          </p>
                          <div className="inventory-batch-placement-buttons inventory-batch-mode-buttons">
                            <button
                              type="button"
                              className={`inventory-batch-placement-btn${batchProductMode === 'batch' ? ' inventory-batch-placement-btn--active' : ''}`}
                              onClick={() => setBatchProductMode('batch')}
                            >
                              One product for the batch
                            </button>
                            <button
                              type="button"
                              className={`inventory-batch-placement-btn${batchProductMode === 'per-item' ? ' inventory-batch-placement-btn--active' : ''}`}
                              onClick={applySuggestedProductsPerItem}
                            >
                              A product per item
                            </button>
                          </div>
                          <div className="inventory-batch-placement-fields">
                            <div className="form-group" style={{ marginBottom: 0 }}>
                              <label className="form-label" htmlFor="batch-country">Country of origin</label>
                              <input
                                id="batch-country"
                                type="text"
                                className="form-input"
                                value={batchCountryOfOrigin}
                                onChange={(e) => setBatchCountryOfOrigin(e.target.value)}
                                placeholder="e.g. USA"
                              />
                            </div>
                            <div className="form-group" style={{ marginBottom: 0 }}>
                              <label className="form-label" htmlFor="batch-product">
                                {batchProductMode === 'per-item' ? 'Product for unset rows' : 'Product'}
                              </label>
                              <select
                                id="batch-product"
                                className="form-input"
                                value={batchProductId}
                                onChange={(e) => setBatchProductId(e.target.value)}
                                required={batchProductMode === 'batch'}
                              >
                                <option value="">Select product</option>
                                {products
                                  .filter((p) => p.catalogStatus !== 'Void')
                                  .map((p) => (
                                    <option key={p.id} value={p.id}>
                                      {p.name} ({p.type})
                                    </option>
                                  ))}
                              </select>
                            </div>
                          </div>
                        </div>
                        <div className="inventory-batch-placement" role="region" aria-label="Where this batch is going">
                          <div className="inventory-batch-placement-toggle" role="group" aria-label="Batch placement">
                            <span className="inventory-batch-placement-heading">Where are these units going?</span>
                            <div className="inventory-batch-placement-buttons">
                              <button
                                type="button"
                                className={`inventory-batch-placement-btn${batchPlacement === 'in-field' ? ' inventory-batch-placement-btn--active' : ''}`}
                                onClick={() => setBatchPlacement('in-field')}
                              >
                                In-field
                              </button>
                              <button
                                type="button"
                                className={`inventory-batch-placement-btn${batchPlacement === 'warehouse' ? ' inventory-batch-placement-btn--active' : ''}`}
                                onClick={() => setBatchPlacement('warehouse')}
                              >
                                Warehouse
                              </button>
                            </div>
                          </div>
                          <p className="inventory-batch-placement-hint">
                            {batchPlacement === 'in-field'
                              ? 'All units will be saved as Deployed. Business and location are merged from the fields below and each row (Edit)—every line must end up with both; you can use only the batch fields, only Edit, or a mix (e.g. one business for all plus per-row locations).'
                              : 'All units will be saved as In Storage. Location below and/or per-row Edit values are merged onto each unit (you can add to inventory without filling these if you do not need locations yet).'}
                          </p>
                          {batchPlacement === 'in-field' ? (
                            <div className="inventory-batch-placement-fields">
                              <div className="form-group" style={{ marginBottom: 0 }}>
                                <label className="form-label" htmlFor="batch-business">
                                  Business name{' '}
                                  <span style={{ fontWeight: 400, color: '#64748b' }}>(optional if set per row)</span>
                                </label>
                                <input
                                  id="batch-business"
                                  type="text"
                                  className="form-input"
                                  value={batchBusiness}
                                  onChange={(e) => setBatchBusiness(e.target.value)}
                                  placeholder="Business"
                                  autoComplete="organization"
                                />
                              </div>
                              <div className="form-group" style={{ marginBottom: 0 }}>
                                <label className="form-label" htmlFor="batch-location-field">
                                  Location{' '}
                                  <span style={{ fontWeight: 400, color: '#64748b' }}>(optional if set per row)</span>
                                </label>
                                <input
                                  id="batch-location-field"
                                  type="text"
                                  className="form-input"
                                  value={batchLocation}
                                  onChange={(e) => setBatchLocation(e.target.value)}
                                  placeholder="City / site / location"
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="inventory-batch-placement-fields">
                              <div className="form-group" style={{ marginBottom: 0 }}>
                                <label className="form-label" htmlFor="batch-location-warehouse">
                                  Location <span style={{ fontWeight: 400, color: '#64748b' }}>(optional)</span>
                                </label>
                                <input
                                  id="batch-location-warehouse"
                                  type="text"
                                  className="form-input"
                                  value={batchLocation}
                                  onChange={(e) => setBatchLocation(e.target.value)}
                                  placeholder="Warehouse / shelf / site"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                        <div
                          ref={batchListPanelFsRef}
                          className="inventory-batch-table-fullscreen-root"
                        >
                          <div className="inventory-batch-table-toolbar">
                            <span className="inventory-batch-table-toolbar-title" id="batch-table-heading">
                              Scanned items ({batchItems.length})
                            </span>
                            <button
                              type="button"
                              className="page-toolbar-btn inventory-batch-fullscreen-btn"
                              onClick={() => void toggleBatchTableFullscreen()}
                              aria-pressed={batchListPanelIsFullscreen}
                              aria-expanded={batchListPanelIsFullscreen}
                              aria-controls="batch-items-table"
                            >
                              <FontAwesomeIcon icon={batchListPanelIsFullscreen ? faCompress : faExpand} />
                              <span className="inventory-batch-fullscreen-btn-label">
                                {batchListPanelIsFullscreen ? 'Exit full screen' : 'Full screen'}
                              </span>
                            </button>
                          </div>
                          <div className="inventory-batch-table-wrapper">
                            <table
                              className="inventory-batch-table"
                              id="batch-items-table"
                              aria-labelledby="batch-table-heading"
                            >
                              <thead>
                                <tr>
                                  <th>Barcode</th>
                                  <th>Country</th>
                                  <th>Product</th>
                                  <th>Serial</th>
                                  <th className="inventory-batch-actions-th">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {batchItems.map((it) => {
                                  const rowProductId = productIdForBatchItem(it);
                                  const rowProduct = products.find((p) => p.id === rowProductId);
                                  const suggested = suggestedProductForItem(it);
                                  return (
                                  <tr
                                    key={it.id}
                                    className={rowProduct ? undefined : 'inventory-batch-row--unresolved'}
                                  >
                                    <td data-label="Barcode">
                                      <code className="inventory-batch-code">{it.rawText}</code>
                                    </td>
                                    <td data-label="Country">{batchCountryOfOrigin.trim() || '—'}</td>
                                    <td data-label="Product">
                                      {batchProductMode === 'per-item' ? (
                                        <select
                                          className="form-input inventory-batch-row-product"
                                          aria-label={`Product for ${it.rawText}`}
                                          value={it.productId}
                                          onChange={(e) => setBatchItemProduct(it.id, e.target.value)}
                                        >
                                          <option value="">
                                            {batchProductId ? 'Use batch product' : 'Select product'}
                                          </option>
                                          {products
                                            .filter((p) => p.catalogStatus !== 'Void')
                                            .map((p) => (
                                              <option key={p.id} value={p.id}>
                                                {p.name} ({p.type})
                                                {suggested?.id === p.id ? ' — suggested' : ''}
                                              </option>
                                            ))}
                                        </select>
                                      ) : rowProduct ? (
                                        <span className="inventory-batch-product-name">{rowProduct.name}</span>
                                      ) : (
                                        <span className="inventory-batch-product-missing">No product selected</span>
                                      )}
                                    </td>
                                    <td data-label="Serial">{it.serialNumber || '—'}</td>
                                    <td data-label="Actions" className="inventory-batch-actions-cell">
                                      <button
                                        type="button"
                                        className="inventory-batch-action inventory-batch-action--edit"
                                        title="Edit"
                                        onClick={() => openBatchItemEdit(it.id)}
                                      >
                                        <FontAwesomeIcon icon={faPenToSquare} /> Edit
                                      </button>
                                      <button
                                        type="button"
                                        className="inventory-batch-action inventory-batch-action--delete"
                                        title="Delete"
                                        onClick={() => deleteBatchItem(it.id)}
                                      >
                                        <FontAwesomeIcon icon={faTrash} /> Delete
                                      </button>
                                    </td>
                                  </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="modal-actions">
                    <button
                      type="button"
                      className="cancel-button"
                      onClick={() => setAddInventoryEntryMode('batch-scan')}
                    >
                      Back to scanner
                    </button>
                    <button type="button" className="cancel-button" onClick={closeAddModal}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="save-button"
                      onClick={handleAddBatchToInventory}
                      disabled={!batchListSaveReady}
                    >
                      Add to Inventory
                    </button>
                  </div>
                </>
              )}

              {addModalChoice === 'add-inventory' && addInventoryEntryMode === 'form' && (() => {
                const selectedProduct = products.find((p) => p.id === addInvProductId);
                const isFullProduct = !!selectedProduct && (selectedProduct.name === 'TIM-E Bot' || selectedProduct.name === 'BIM-E bot');
                const showStockOnlyForm =
                  !!selectedProduct && selectedProduct.type === 'Stock Item' && !editingBatchItemId;
                const goBackToAddInventoryChoose = () => {
                  if (editingBatchItemId) {
                    setEditingBatchItemId(null);
                    resetInventoryForm();
                    setAddInventoryEntryMode('batch-list');
                    return;
                  }
                  setAddInventoryEntryMode('choose');
                  setBarcodeParseError(null);
                };
                return (
                  <form onSubmit={handleAddInventory} className="inventory-add-form">
                    <div className="modal-body">
                    <div className="inventory-modal-card">
                      <h4 className="inventory-modal-card-title">Product</h4>
                      <div className="inventory-modal-card-fields">
                        <div className="form-group">
                          <label className="form-label">Product</label>
                          <select
                            className="form-input"
                            value={addInvProductId}
                            onChange={(e) => {
                              setAddInvProductId(e.target.value);
                              setAddInvProductSuggestedFrom('');
                            }}
                            required
                          >
                            <option value="">Select product</option>
                            {products
                              .filter((p) => p.catalogStatus !== 'Void')
                              .map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.name} ({p.type})
                                </option>
                              ))}
                          </select>
                          {addInvProductSuggestedFrom && addInvProductId && (
                            <p className="form-hint">
                              Suggested from the {addInvProductSuggestedFrom} code on the tag. Pick a different
                              product if that is not what was scanned.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {showStockOnlyForm ? (
                      <div className="inventory-modal-card">
                        <h4 className="inventory-modal-card-title">Stock</h4>
                        <p className="inventory-stock-hint">
                          Same manufacturer, location, and Deployed setting as an existing row increases the count
                          for that row (no barcodes). Manufacturer and location are optional; leave blank to add to
                          the unnamed placement row.
                        </p>
                        <div className="inventory-modal-card-fields">
                          <div className="form-group">
                            <label className="form-label">Location</label>
                            <input
                              type="text"
                              className="form-input"
                              value={addInvLocation}
                              onChange={(e) => setAddInvLocation(e.target.value)}
                              placeholder="Warehouse / site / shelf (optional)"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Manufacturer</label>
                            <input
                              type="text"
                              className="form-input"
                              value={addInvManufacturer}
                              onChange={(e) => setAddInvManufacturer(e.target.value)}
                              placeholder="Manufacturer (optional)"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Deployed</label>
                            <select
                              className="form-input"
                              value={addInvStockDeployed ? 'yes' : 'no'}
                              onChange={(e) => setAddInvStockDeployed(e.target.value === 'yes')}
                            >
                              <option value="no">No</option>
                              <option value="yes">Yes</option>
                            </select>
                            <p className="form-hint">
                              Yes if these items are out at a business. No keeps them counted as available
                              in an office or warehouse.
                            </p>
                          </div>
                          <div className="form-group">
                            <label className="form-label">Count</label>
                            <input
                              type="number"
                              min={1}
                              className="form-input"
                              value={addInvQuantity}
                              onChange={(e) => setAddInvQuantity(e.target.value)}
                              placeholder="1"
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                    <div className="inventory-modal-card">
                      <h4 className="inventory-modal-card-title">Location &amp; status</h4>
                      <div className="inventory-modal-card-fields">
                        <div className="form-group">
                          <label className="form-label">Location</label>
                          <input type="text" className="form-input" value={addInvLocation} onChange={(e) => setAddInvLocation(e.target.value)} placeholder="City / location" />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Business</label>
                          <input type="text" className="form-input" value={addInvBusiness} onChange={(e) => setAddInvBusiness(e.target.value)} placeholder="Business" />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Installation Date</label>
                          <input type="date" className="form-input" value={addInvInstallDate} onChange={(e) => setAddInvInstallDate(e.target.value)} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Status</label>
                          <select className="form-input" value={addInvStatus} onChange={(e) => setAddInvStatus(e.target.value as InventoryUnitStatus | '')}>
                            <option value="">—</option>
                            <option value="Deployed">Deployed</option>
                            <option value="In Storage">In Storage</option>
                            <option value="Repair">Repair</option>
                            <option value="Out of Order">Out of Order</option>
                            <option value="Event">Event</option>
                            <option value="In-Route">In-Route</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Condition</label>
                          <select className="form-input" value={addInvCondition} onChange={(e) => setAddInvCondition(e.target.value as InventoryUnitCondition | '')}>
                            <option value="">—</option>
                            <option value="Working">Working</option>
                            <option value="Broken">Broken</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {isFullProduct && (
                      <div className="inventory-modal-card">
                        <h4 className="inventory-modal-card-title">Equipment counts</h4>
                        <div className="inventory-modal-card-fields inventory-modal-card-grid">
                          <div className="form-group">
                            <label className="form-label">Trash bins</label>
                            <input type="number" min={0} className="form-input" value={addInvTrashBins} onChange={(e) => setAddInvTrashBins(e.target.value)} placeholder="0" />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Linen bins</label>
                            <input type="number" min={0} className="form-input" value={addInvLinenBins} onChange={(e) => setAddInvLinenBins(e.target.value)} placeholder="0" />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Rolling bases</label>
                            <input type="number" min={0} className="form-input" value={addInvRollingBases} onChange={(e) => setAddInvRollingBases(e.target.value)} placeholder="0" />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Static bases</label>
                            <input type="number" min={0} className="form-input" value={addInvStaticBases} onChange={(e) => setAddInvStaticBases(e.target.value)} placeholder="0" />
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="inventory-modal-card">
                      <h4 className="inventory-modal-card-title">Product details</h4>
                      <div className="inventory-modal-card-fields">
                        {!isFullProduct && (
                          <div className="form-group">
                            <label className="form-label">Quantity</label>
                            <input type="number" min={1} className="form-input" value={addInvQuantity} onChange={(e) => setAddInvQuantity(e.target.value)} placeholder="1" />
                          </div>
                        )}
                        <div className="form-group">
                          <label className="form-label">Country of origin</label>
                          <input type="text" className="form-input" value={addInvCountry} onChange={(e) => setAddInvCountry(e.target.value)} placeholder="e.g. USA" />
                        </div>
                        <div className="form-group">
                          <label className="form-label">
                            Product type{' '}
                            <span style={{ fontWeight: 400, color: '#8A8F93' }}>(asset tag category, not catalog product)</span>
                          </label>
                          <input
                            type="text"
                            className="form-input"
                            value={addInvProductType}
                            onChange={(e) => setAddInvProductType(e.target.value)}
                            placeholder="e.g. Garg"
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Serial number</label>
                          <input type="text" className="form-input" value={addInvSerial} onChange={(e) => setAddInvSerial(e.target.value)} placeholder={isFullProduct ? 'Optional' : 'Optional; used as base for multiple units'} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Model</label>
                          <input type="text" className="form-input" value={addInvModel} onChange={(e) => setAddInvModel(e.target.value)} placeholder="Model" />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Manufacturer</label>
                          <input type="text" className="form-input" value={addInvManufacturer} onChange={(e) => setAddInvManufacturer(e.target.value)} placeholder="Manufacturer" />
                        </div>
                      </div>
                    </div>
                      </>
                    )}

                    </div>

                    <div className="modal-actions">
                      <button type="button" className="cancel-button" onClick={goBackToAddInventoryChoose}>
                        {editingBatchItemId ? 'Back to batch' : 'Change method'}
                      </button>
                      <button type="button" className="cancel-button" onClick={closeAddModal}>Cancel</button>
                      <button type="submit" className="save-button">
                        {editingBatchItemId ? 'Save changes' : 'Add Inventory'}
                      </button>
                    </div>
                  </form>
                );
              })()}
      </Modal>

      <Modal
        isOpen={duplicateBarcodeInfo !== null}
        onClose={() => setDuplicateBarcodeInfo(null)}
        title="Barcode already in inventory"
        narrow
        elevated
      >
        <div className="modal-body">
          <p style={{ margin: '0 0 0.75rem', color: '#374151', fontSize: '0.9375rem' }}>
            This asset tag matches an item that is already in inventory.
          </p>
          {duplicateBarcodeInfo && (
            <ul className="inventory-duplicate-barcode-details">
              <li>
                <span className="inventory-duplicate-barcode-label">Product</span>
                {duplicateBarcodeInfo.productName}
              </li>
              <li>
                <span className="inventory-duplicate-barcode-label">Country</span>
                {duplicateBarcodeInfo.countryOfOrigin || '—'}
              </li>
              <li>
                <span className="inventory-duplicate-barcode-label">Product type</span>
                {duplicateBarcodeInfo.productType || '—'}
              </li>
              <li>
                <span className="inventory-duplicate-barcode-label">Serial</span>
                {duplicateBarcodeInfo.serialNumber}
              </li>
            </ul>
          )}
        </div>
        <div className="modal-actions">
          <button
            type="button"
            className="cancel-button"
            onClick={() => {
              setDuplicateBarcodeInfo(null);
              setBarcodeScanRemountKey((k) => k + 1);
            }}
          >
            Scan another
          </button>
          <button
            type="button"
            className="save-button"
            onClick={() => {
              if (!duplicateBarcodeInfo) return;
              navigate(`/inventory/product/${duplicateBarcodeInfo.productId}`);
              setDuplicateBarcodeInfo(null);
              closeAddModal();
            }}
          >
            View product
          </button>
        </div>
      </Modal>

      {/* Report type chooser */}
      <Modal isOpen={reportFlow === 'choose'} onClose={closeReportFlow} title="Generate report">
        <div className="modal-body">
          <div className="inventory-add-choices">
            <p className="inventory-add-prompt">Which report would you like to generate?</p>
            <button type="button" className="inventory-add-choice-button" onClick={startInventoryReport}>
              Inventory report
              <span className="inventory-add-choice-sub">Monthly inventory totals; choose which products to include.</span>
            </button>
            <button type="button" className="inventory-add-choice-button" onClick={() => setReportFlow('tim-e')}>
              TIM-E report
              <span className="inventory-add-choice-sub">Inventory units for the TIM-E Bot, with installation dates.</span>
            </button>
            <button type="button" className="inventory-add-choice-button" onClick={() => setReportFlow('bim-e')}>
              BIM-E report
              <span className="inventory-add-choice-sub">Inventory units for the BIM-E bot, with installation dates.</span>
            </button>
            <button type="button" className="inventory-add-choice-button" onClick={startFfeReport}>
              FF&amp;E report
              <span className="inventory-add-choice-sub">Furniture, fixtures, and equipment; choose which items to include.</span>
            </button>
          </div>
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" onClick={closeReportFlow}>
            Cancel
          </button>
        </div>
      </Modal>

      {/* Inventory report — product selection */}
      <Modal
        isOpen={reportFlow === 'inventory-select'}
        onClose={closeReportFlow}
        title="Select products for report"
        wide
      >
        <div className="modal-body">
          <p className="inventory-add-prompt" style={{ marginBottom: '0.75rem' }}>
            All products are included by default. Uncheck any you want to leave out of the report.
          </p>
          <div className="inventory-report-select-toolbar">
            <button type="button" className="page-toolbar-btn" onClick={() => setAllReportProducts(true)}>
              Select all
            </button>
            <button type="button" className="page-toolbar-btn" onClick={() => setAllReportProducts(false)}>
              Clear all
            </button>
            <span className="inventory-report-select-count">
              {reportSelectedIds.size} of {reportSelectableProducts.length} selected
            </span>
          </div>
          <div className="inventory-report-select-list">
            {reportSelectableProducts.map((p) => (
              <label key={p.id} className="inventory-report-select-item">
                <input
                  type="checkbox"
                  checked={reportSelectedIds.has(p.id)}
                  onChange={() => toggleReportProduct(p.id)}
                />
                <span className="inventory-report-select-name">{p.name}</span>
                <span className="inventory-report-select-type">{p.type}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" onClick={() => setReportFlow('choose')}>
            Back
          </button>
          <button type="button" className="cancel-button" onClick={closeReportFlow}>
            Cancel
          </button>
          <button
            type="button"
            className="save-button"
            onClick={() => setReportFlow('inventory')}
            disabled={reportSelectedIds.size === 0}
          >
            Generate
          </button>
        </div>
      </Modal>

      {/* FF&E report — item selection (same picker as the inventory report) */}
      <Modal
        isOpen={reportFlow === 'ffe-select'}
        onClose={closeReportFlow}
        title="Select FF&E items for report"
        wide
      >
        <div className="modal-body">
          <p className="inventory-add-prompt" style={{ marginBottom: '0.75rem' }}>
            All FF&amp;E items are included by default. Uncheck any you want to leave out of the report.
          </p>
          {reportSelectableFfe.length === 0 ? (
            <p className="page-subtitle">No FF&amp;E items to include. Add items to the FF&amp;E inventory first.</p>
          ) : (
            <>
              <div className="inventory-report-select-toolbar">
                <button type="button" className="page-toolbar-btn" onClick={() => setAllReportFfeItems(true)}>
                  Select all
                </button>
                <button type="button" className="page-toolbar-btn" onClick={() => setAllReportFfeItems(false)}>
                  Clear all
                </button>
                <span className="inventory-report-select-count">
                  {reportSelectedFfeIndexes.size} of {reportSelectableFfe.length} selected
                </span>
              </div>
              <div className="inventory-report-select-list">
                {reportSelectableFfe.map(({ row, index }) => (
                  <label key={index} className="inventory-report-select-item">
                    <input
                      type="checkbox"
                      checked={reportSelectedFfeIndexes.has(index)}
                      onChange={() => toggleReportFfeItem(index)}
                    />
                    <span className="inventory-report-select-name">{row.product}</span>
                    <span className="inventory-report-select-type">
                      {row.available || '0'} available
                    </span>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" onClick={() => setReportFlow('choose')}>
            Back
          </button>
          <button type="button" className="cancel-button" onClick={closeReportFlow}>
            Cancel
          </button>
          <button
            type="button"
            className="save-button"
            onClick={() => setReportFlow('ffe')}
            disabled={reportSelectedFfeIndexes.size === 0}
          >
            Generate
          </button>
        </div>
      </Modal>

      <MonthlyInventoryReportModal
        isOpen={reportFlow === 'inventory'}
        onClose={closeReportFlow}
        timEParts={reportTimEParts}
        catalogProducts={reportFilteredProducts}
        inventoryUnits={units}
      />

      <FfeInventoryReportModal
        isOpen={reportFlow === 'ffe'}
        onClose={closeReportFlow}
        items={reportFilteredFfe}
      />

      <ProductUnitsReportModal
        isOpen={reportFlow === 'tim-e'}
        onClose={closeReportFlow}
        title="TIM-E Inventory Report"
        units={timEReportUnits}
      />

      <ProductUnitsReportModal
        isOpen={reportFlow === 'bim-e'}
        onClose={closeReportFlow}
        title="BIM-E Inventory Report"
        units={bimEReportUnits}
      />

      <Modal
        isOpen={operationsEditIndex !== null}
        onClose={closeOperationsEdit}
        title={`FF&E Item — ${operationsEditIndex !== null ? (operations[operationsEditIndex]?.product || 'New item') : ''}`}
        wide
      >
        <form onSubmit={handleSaveOperationsEdit} className="inventory-add-form">
          <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Product</label>
                <input
                  type="text"
                  className="form-input"
                  value={editProduct}
                  onChange={(e) => setEditProduct(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Available</label>
                <input
                  type="text"
                  className="form-input"
                  value={editAvailable}
                  onChange={(e) => setEditAvailable(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Replacement Cost per item</label>
                <input
                  type="text"
                  className="form-input"
                  value={editReplacementCost}
                  onChange={(e) => setEditReplacementCost(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Value</label>
                <input
                  type="text"
                  className="form-input"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                />
              </div>

              <div className="ffe-documents-section">
                <h4 className="ffe-documents-title">Receipts &amp; Warranties</h4>
                <p className="ffe-documents-hint">Keep copies of receipts and warranty documents for this item.</p>
                <div className="ffe-doc-drop-grid">
                  <div className="ffe-doc-drop-cell">
                    <span className="ffe-doc-drop-label">Receipt</span>
                    <FileDropZone
                      variant="compact"
                      accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                      maxFileSizeBytes={MAX_FFE_FILE_SIZE}
                      onReject={(msg) => alert(msg)}
                      prompt={<>Drop receipt here or <span className="file-drop-zone__browse-word">browse</span></>}
                      hint="PDF, images, or Word — max 2 MB"
                      onFiles={(files) => {
                        const f = files[0];
                        if (f) uploadFfeDoc(f, 'receipt');
                      }}
                    />
                  </div>
                  <div className="ffe-doc-drop-cell">
                    <span className="ffe-doc-drop-label">Warranty</span>
                    <FileDropZone
                      variant="compact"
                      accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                      maxFileSizeBytes={MAX_FFE_FILE_SIZE}
                      onReject={(msg) => alert(msg)}
                      prompt={<>Drop warranty here or <span className="file-drop-zone__browse-word">browse</span></>}
                      hint="PDF, images, or Word — max 2 MB"
                      onFiles={(files) => {
                        const f = files[0];
                        if (f) uploadFfeDoc(f, 'warranty');
                      }}
                    />
                  </div>
                </div>
                <div className="ffe-documents-table-wrapper">
                  <table className="ffe-documents-table">
                    <thead>
                      <tr>
                        <SortableTh label="File name" columnKey="file" sortKey={ffeDocsSort.sortKey} sortDir={ffeDocsSort.sortDir} onSort={ffeDocsSort.toggleSort} />
                        <SortableTh label="Type" columnKey="type" sortKey={ffeDocsSort.sortKey} sortDir={ffeDocsSort.sortDir} onSort={ffeDocsSort.toggleSort} />
                        <StaticTh>Actions</StaticTh>
                      </tr>
                    </thead>
                    <tbody>
                      {ffeDocumentsForEdit.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="ffe-docs-empty">No documents yet. Use the buttons above to upload.</td>
                        </tr>
                      ) : (
                        sortedFfeDocumentsForEdit.map((doc) => (
                          <tr key={doc.id} className={isVoided(doc) ? 'attachment-voided' : undefined}>
                            <td data-label="File name">
                              {doc.fileName} {isVoided(doc) ? <AttachmentVoidPill /> : null}
                              <AttachmentVoidReason attachment={doc} />
                            </td>
                            <td data-label="Type">{doc.type === 'receipt' ? 'Receipt' : 'Warranty'}</td>
                            <td data-label="Actions">
                              <a href={doc.dataUrl} download={doc.fileName} className="ffe-doc-link" title="Download"><FontAwesomeIcon icon={faDownload} /></a>
                              {' · '}
                              <button
                                type="button"
                                className={isVoided(doc) ? 'attachment-unvoid-btn' : 'attachment-void-btn'}
                                disabled={ffeDocVoid.busy}
                                onClick={() => (isVoided(doc) ? ffeDocVoid.unvoid(doc) : ffeDocVoid.requestVoid(doc))}
                              >
                                {isVoided(doc) ? 'Unvoid' : 'Void'}
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
          </div>

              <div className="modal-actions">
                <button type="button" className="cancel-button" onClick={closeOperationsEdit}>Cancel</button>
                <button type="submit" className="save-button">Save</button>
              </div>
        </form>
      </Modal>

      {/* Receipts and warranties are voided from inside the item modal, so stack above it. */}
      <AttachmentVoidModal control={ffeDocVoid} elevated />
    </div>
  );
};

export default Inventory;
